import { useMemo, useState } from 'react';

import { Button, Input } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import { useStrings } from '../../../hooks/useStrings';
import { useStepTiming } from '../../../lib/analytics';
import { useCovenantStore } from '../../../stores/covenant.store';
import { IntroScreen } from '../intro-screen';
import { IntroMark } from '../intro-mark';
import { SuggestionChips } from '../suggestion-chips';

/** Where the resolved safe word came from at the moment the user submitted. */
export type SafeWordSource = 'chip' | 'custom' | 'none';

interface IntroPactProps {
  onBack: () => void;
  /** Called after the safe-word (or empty skip) has been written to the covenant store. */
  onComplete: (source: SafeWordSource) => void;
  onLogin: () => void;
}

export function IntroPact({ onBack, onComplete, onLogin }: IntroPactProps) {
  useStepTiming(FlowSteps.IntroScreen5);
  const { strings } = useStrings();
  const setSafeWord = useCovenantStore((s) => s.setSafeWord);

  // Seed the input from the persisted safe word so back-navigation feels
  // continuous — but treat any user keystroke as a custom override.
  const persisted = useCovenantStore((s) => s.safeWord);
  const initialFromChip = useMemo(() => {
    const list = strings.intro.screen4.suggestions;
    return list.includes(persisted) ? persisted : null;
  }, [persisted, strings.intro.screen4.suggestions]);

  const [selected, setSelected] = useState<string | null>(initialFromChip);
  const [custom, setCustom] = useState(initialFromChip ? '' : persisted);

  const resolved = (custom.trim() || selected || '').trim();

  const begin = () => {
    let source: SafeWordSource;
    if (custom.trim()) source = 'custom';
    else if (selected) source = 'chip';
    else source = 'none';
    setSafeWord(resolved);
    onComplete(source);
  };

  const skip = () => {
    setSafeWord('');
    onComplete('none');
  };

  return (
    <IntroScreen
      step={5}
      tag={strings.intro.screen4.tag}
      title={strings.intro.screen4.title}
      italic={strings.intro.screen4.italic}
      body={strings.intro.screen4.body}
      visual={<IntroMark variant="cocoon" />}
      onBack={onBack}
      onLogin={onLogin}
      onNext={begin}
      nextLabel={strings.intro.finalCta}
      nextTone="emerald"
      extra={
        <div className="space-y-3">
          <SuggestionChips
            suggestions={strings.intro.screen4.suggestions}
            selected={selected}
            onSelect={(word) => {
              setSelected(word);
              setCustom('');
            }}
          />
          <Input
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value);
              if (selected) setSelected(null);
            }}
            placeholder={strings.intro.screen4.customPlaceholder}
            className="rounded-full bg-background text-center"
            aria-label={strings.intro.screen4.customPlaceholder}
          />
        </div>
      }
      secondaryCta={
        <Button
          variant="ghost"
          onClick={skip}
          className="w-full text-xs text-muted-foreground hover:text-foreground"
        >
          {strings.intro.screen4.skipCta}
        </Button>
      }
    />
  );
}
